import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
	type StatusBarItem,
	useWorkspacePage,
} from "@/components/workspace";
import type { FiscalPeriod } from "@/gen/omnios/v1/accounting_pb";
import { PeriodStatus } from "@/gen/omnios/v1/accounting_pb";
import { formatDate } from "@/lib/format";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { AG_GRID_LOCALE_RU } from "@/lib/ag-grid-locale-ru";
import { useGridLayout } from "@/lib/use-grid-layout";
import { useServerGrid } from "@/lib/use-server-grid";
import { accountingClient } from "@/lib/accounting-client";

import type { ColDef, ValueFormatterParams } from "@ag-grid-community/core";
import { AgGridReact } from "@ag-grid-community/react";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-quartz.css";

import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/accounting/close")({
	component: PeriodClosePage,
});

const PERIOD_STATUS_KEYS: Record<number, string> = {
	[PeriodStatus.OPEN]: "period_open",
	[PeriodStatus.SOFT_CLOSED]: "period_soft_closed",
	[PeriodStatus.CLOSED]: "period_closed",
	[PeriodStatus.REOPENED]: "period_reopened",
};

const PERIOD_STATUS_COLORS: Record<number, string> = {
	[PeriodStatus.OPEN]: "hsl(var(--chart-2))",
	[PeriodStatus.SOFT_CLOSED]: "#ca8a04",
	[PeriodStatus.CLOSED]: "hsl(var(--muted-foreground))",
	[PeriodStatus.REOPENED]: "hsl(var(--destructive))",
};

function PeriodClosePage() {
	const { t } = useTranslation("accounting");
	useBreadcrumbs([{ label: t("nav_close") }]);
	const navigate = useNavigate();
	const { onGridReady, onStateChanged } = useGridLayout("close");
	const [statusFilter, setStatusFilter] = useState("all");

	const extraFilter = useMemo(() => {
		const f: Record<string, unknown> = {};
		if (statusFilter !== "all") f.status = Number(statusFilter);
		return f;
	}, [statusFilter]);

	const { gridRef, gridProps, totalRows, refreshGrid } = useServerGrid<FiscalPeriod>({
		colToField: {
			startDate: "start_date",
			endDate: "end_date",
			closedAt: "closed_at",
		},
		fetchRows: async ({ pageSize, pageToken, sort }) => {
			const resp = await accountingClient.listPeriods({
				page: { pageSize, pageToken },
				sort: sort ? { field: sort.field, descending: sort.descending } : undefined,
				status: statusFilter !== "all" ? Number(statusFilter) : undefined,
			});
			return {
				rows: resp.periods,
				nextPageToken: resp.page?.nextPageToken,
				totalCount: resp.page?.totalCount ?? 0,
			};
		},
		extraFilter,
	});

	const columnDefs = useMemo<ColDef<FiscalPeriod>[]>(
		() => [
			{
				field: "periodId",
				headerName: t("entry_period"),
				width: 120,
				pinned: "left" as const,
				cellClass: "font-medium",
			},
			{
				field: "startDate",
				headerName: t("entry_date") + " (" + t("opening_balance_short") + ")",
				width: 110,
				filter: "agDateColumnFilter",
				valueFormatter: (p: ValueFormatterParams<FiscalPeriod>) => {
					const d = p.data?.startDate;
					return d ? formatDate(d.toDate(), "dd.MM.yyyy") : "\u2014";
				},
			},
			{
				field: "endDate",
				headerName: t("entry_date") + " (" + t("closing_balance_short") + ")",
				width: 110,
				filter: "agDateColumnFilter",
				valueFormatter: (p: ValueFormatterParams<FiscalPeriod>) => {
					const d = p.data?.endDate;
					return d ? formatDate(d.toDate(), "dd.MM.yyyy") : "\u2014";
				},
			},
			{
				field: "status",
				headerName: t("entry_status"),
				width: 130,
				filter: false,
				cellRenderer: (p: { value: number }) => {
					const label = t(PERIOD_STATUS_KEYS[p.value] ?? "period_open");
					const color = PERIOD_STATUS_COLORS[p.value] ?? "hsl(var(--muted-foreground))";
					return <span style={{ color }}>{label}</span>;
				},
			},
			{
				headerName: t("close_progress"),
				width: 120,
				filter: false,
				valueGetter: (p) => p.data?.closeChecklist?.progressPct ?? 0,
				cellRenderer: (p: { value: number }) => {
					const pct = p.value;
					return (
						<div style={{ display: "flex", alignItems: "center", gap: 6, height: "100%" }}>
							<div style={{ flex: 1, height: 6, background: "hsl(var(--border))", borderRadius: 3, overflow: "hidden" }}>
								<div style={{ width: `${pct}%`, height: "100%", background: "hsl(var(--chart-2))", borderRadius: 3 }} />
							</div>
							<span style={{ fontSize: 11, color: "hsl(var(--muted-foreground))", fontVariantNumeric: "tabular-nums" }}>{pct}%</span>
						</div>
					);
				},
			},
			{
				field: "closedBy",
				headerName: t("close_closed_by"),
				width: 120,
				valueFormatter: (p: ValueFormatterParams<FiscalPeriod>) =>
					p.value || "\u2014",
			},
			{
				field: "closedAt",
				headerName: t("close_closed_at"),
				width: 140,
				filter: "agDateColumnFilter",
				valueFormatter: (p: ValueFormatterParams<FiscalPeriod>) => {
					const d = p.data?.closedAt;
					return d ? formatDate(d.toDate(), "dd.MM.yyyy HH:mm") : "\u2014";
				},
			},
		],
		[t],
	);

	const defaultColDef = useMemo<ColDef>(
		() => ({
			sortable: true,
			resizable: true,
			filter: true,
			filterParams: {
				buttons: ["apply", "reset"],
				closeOnApply: true,
			},
		}),
		[],
	);

	const onRowClicked = useCallback(
		(e: { data: FiscalPeriod | undefined }) => {
			if (e.data) {
				navigate({
					to: "/accounting/close/$periodId",
					params: { periodId: e.data.periodId },
				});
			}
		},
		[navigate],
	);

	const statusBarItems = useMemo<StatusBarItem[]>(
		() => [
			{
				id: "count",
				content: t("row_count", { count: totalRows }),
				align: "left" as const,
			},
		],
		[totalRows, t],
	);

	useWorkspacePage({ statusBar: statusBarItems });

	return (
		<div className="flex flex-col h-full overflow-hidden">
			<div className="flex items-center gap-2 px-4 py-2 shrink-0">
				<div className="relative flex-1">
					<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder={t("search_placeholder", { defaultValue: "Поиск..." })}
						onChange={(e) =>
							gridRef.current?.api?.setGridOption(
								"quickFilterText",
								e.target.value,
							)
						}
						className="pl-9 h-9"
					/>
				</div>
				<Button
					variant={statusFilter === "all" ? "default" : "outline"}
					size="sm"
					className="h-9"
					onClick={() => setStatusFilter("all")}
				>
					{t("filter_all", { defaultValue: "Все" })}
				</Button>
				<Button
					variant={statusFilter === "1" ? "default" : "outline"}
					size="sm"
					className="h-9"
					onClick={() => setStatusFilter("1")}
				>
					{t("period_open")}
				</Button>
				<Button
					variant={statusFilter === "3" ? "default" : "outline"}
					size="sm"
					className="h-9"
					onClick={() => setStatusFilter("3")}
				>
					{t("period_closed")}
				</Button>
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() => gridRef.current?.api?.exportDataAsCsv()}
				>
					CSV
				</Button>
			</div>
			<div className="flex-1 ag-theme-quartz" style={{ width: "100%" }}>
				<AgGridReact<FiscalPeriod>
					ref={gridRef}
					{...gridProps}
					columnDefs={columnDefs}
					defaultColDef={defaultColDef}
					localeText={AG_GRID_LOCALE_RU}
					onGridReady={onGridReady}
					onSortChanged={() => {
						onStateChanged();
						refreshGrid();
					}}
					onFilterChanged={() => { onStateChanged(); refreshGrid(); }}
					onColumnMoved={onStateChanged}
					onColumnResized={onStateChanged}
					onColumnVisible={onStateChanged}
					rowHeight={28}
					headerHeight={32}
					animateRows={false}
					getRowId={(p) => p.data.id}
					onRowClicked={onRowClicked}
					rowClass="cursor-pointer"
					overlayNoRowsTemplate={`<div style="text-align:center;padding:40px"><p style="font-size:13px;font-weight:500">${t("empty_periods_title", { defaultValue: "Нет периодов" })}</p><p style="font-size:11px;color:hsl(var(--muted-foreground));margin-top:4px">${t("empty_periods_desc", { defaultValue: "Периоды создаются автоматически при проводке" })}</p></div>`}
				/>
			</div>
		</div>
	);
}
